import React, { useState, useCallback, useEffect } from 'react';
import { X, CalendarSearch, PawPrint, User, CheckCircle, Loader2, ChevronRight, ChevronLeft, AlertTriangle } from 'lucide-react';
import AvailabilityPanel from './AvailabilityPanel';
import { bookAppointment, fetchServices } from '../services/daysmartService';

/**
 * Sticky slide-out drawer that wraps the AvailabilityPanel.
 * Designed to live at the page level (e.g. CommunicationCenter) so it persists
 * while the user browses through communication chains.
 *
 * Props:
 *   isOpen          - Whether the drawer is visible
 *   onClose         - Called when the user closes the drawer
 *   bookingContext  - { clientId, clientName, locationId, pet_name, pet_id,
 *                      service_name, employee_name, duration_minutes }
 *   onBookingComplete - Optional callback after a successful booking
 */
const StickyAvailabilityDrawer = ({ isOpen, onClose, bookingContext, onBookingComplete }) => {
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [bookingState, setBookingState] = useState('idle'); // idle, booking, success, error
    const [bookingError, setBookingError] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [services, setServices] = useState([]);
    const [manualServiceId, setManualServiceId] = useState('');

    const ctx = bookingContext || {};
    const effectiveServiceId = ctx.service_id || manualServiceId || null;
    const selectedService = services.find(s => s.service_id === manualServiceId);

    const handleSlotSelect = useCallback((slot) => {
        setSelectedSlot(slot);
        setBookingState('idle');
        setBookingError(null);
    }, []);

    const handleBookAppointment = async () => {
        if (!selectedSlot || !ctx.clientId) return;

        setBookingState('booking');
        setBookingError(null);

        try {
            await bookAppointment({
                location_id: ctx.locationId,
                client_id: ctx.clientId,
                pet_id: ctx.pet_id || undefined,
                pet_name: ctx.pet_name || undefined,
                employee_id: selectedSlot.employee_id,
                start_datetime: selectedSlot.start_time,
                end_datetime: selectedSlot.end_time,
                service_name: selectedService?.name || ctx.service_name || 'Grooming',
                service_id: effectiveServiceId || undefined,
                additional_services: ctx.additional_services || undefined,
                additional_pets: ctx.additional_pets?.map(p => ({
                    ...p,
                    start_datetime: selectedSlot.start_time,
                    end_datetime: selectedSlot.end_time,
                })) || undefined,
            });
            setBookingState('success');
            setSelectedSlot(null);
            if (onBookingComplete) onBookingComplete();
        } catch (err) {
            console.error('Failed to book appointment:', err);
            setBookingState('error');
            setBookingError(err.message || 'Failed to book appointment');
        }
    };

    const handleClose = () => {
        setSelectedSlot(null);
        setBookingState('idle');
        setBookingError(null);
        setCollapsed(false);
        onClose();
    };

    // Reset booking state when context changes (new client)
    React.useEffect(() => {
        setSelectedSlot(null);
        setBookingState('idle');
        setBookingError(null);
        setManualServiceId('');
    }, [ctx.clientId, ctx.locationId]);

    // Load service list when no service_id from history
    useEffect(() => {
        if (ctx.service_id || !ctx.locationId) return;
        let cancelled = false;
        fetchServices(ctx.locationId)
            .then(data => { if (!cancelled) setServices(data.services || []); })
            .catch(err => console.warn('Failed to load services:', err));
        return () => { cancelled = true; };
    }, [ctx.locationId, ctx.service_id]);

    if (!isOpen) return null;

    return (
        <div
            className={`fixed top-0 right-0 h-full bg-white shadow-2xl border-l border-gray-200 z-40 flex flex-col transition-all duration-300 ease-in-out ${
                collapsed ? 'w-12' : 'w-[520px]'
            }`}
        >
            {/* Collapse toggle */}
            <button
                onClick={() => setCollapsed(!collapsed)}
                className="absolute -left-8 top-1/2 -translate-y-1/2 w-8 h-16 bg-purple-600 text-white rounded-l-lg flex items-center justify-center hover:bg-purple-700 transition-colors shadow-lg"
                title={collapsed ? 'Expand availability panel' : 'Collapse availability panel'}
            >
                {collapsed ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>

            {collapsed ? (
                <div className="flex flex-col items-center pt-4 gap-2">
                    <CalendarSearch size={20} className="text-purple-600" />
                    <span className="text-[10px] text-purple-600 font-medium writing-mode-vertical"
                          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                        Availability
                    </span>
                </div>
            ) : (
                <>
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b bg-purple-50 shrink-0">
                        <div className="flex items-center gap-2 min-w-0">
                            <CalendarSearch size={18} className="text-purple-600 shrink-0" />
                            <div className="min-w-0">
                                <h3 className="text-sm font-semibold text-purple-800 truncate">
                                    Groomer Availability
                                </h3>
                                {ctx.clientName && (
                                    <p className="text-xs text-purple-600 truncate">
                                        for {ctx.clientName}
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Booking context summary */}
                    {(ctx.pet_name || ctx.service_name || ctx.employee_name) && (
                        <div className="px-4 py-2 border-b bg-purple-50/50 shrink-0">
                            <p className="text-[11px] font-medium text-purple-700 mb-1">Based on history:</p>
                            <div className="flex items-center gap-1.5 text-xs text-gray-700 flex-wrap">
                                {ctx.pet_name && (
                                    <span className="flex items-center gap-0.5 font-medium">
                                        <PawPrint size={10} className="text-purple-500" />
                                        {ctx.pet_name}
                                    </span>
                                )}
                                {ctx.service_name && (
                                    <span className="text-gray-500">— {ctx.service_name}</span>
                                )}
                                {ctx.duration_minutes && (
                                    <span className="text-gray-400">
                                        ({ctx.duration_minutes >= 60
                                            ? `${Math.floor(ctx.duration_minutes / 60)}hr${ctx.duration_minutes % 60 ? ` ${ctx.duration_minutes % 60}m` : ''}`
                                            : `${ctx.duration_minutes}m`})
                                    </span>
                                )}
                            </div>
                            {ctx.additional_services && ctx.additional_services.length > 0 && (
                                <p className="text-[11px] text-gray-500 mt-0.5">
                                    + {ctx.additional_services.map(s => s.service_name).join(', ')}
                                </p>
                            )}
                            {ctx.additional_pets && ctx.additional_pets.length > 0 && (
                                <div className="mt-1 pt-1 border-t border-purple-100">
                                    {ctx.additional_pets.map((p, i) => (
                                        <div key={i} className="flex items-center gap-1.5 text-xs text-gray-700">
                                            <PawPrint size={10} className="text-purple-400" />
                                            <span className="font-medium">{p.pet_name}</span>
                                            {p.service_name && <span className="text-gray-500">— {p.service_name}</span>}
                                            {p.additional_services && p.additional_services.length > 0 && (
                                                <span className="text-[11px] text-gray-400">
                                                    + {p.additional_services.map(s => s.service_name).join(', ')}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {ctx.employee_name && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                    <User size={9} className="inline mr-0.5" />
                                    Last with {ctx.employee_name}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Booking success banner */}
                    {bookingState === 'success' && (
                        <div className="px-4 py-3 bg-green-50 border-b border-green-200 shrink-0">
                            <div className="flex items-center gap-2 text-green-700">
                                <CheckCircle size={16} />
                                <span className="text-sm font-medium">Appointment booked!</span>
                            </div>
                            <button
                                onClick={() => setBookingState('idle')}
                                className="mt-1 text-xs text-green-600 hover:text-green-800 underline"
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    {/* Booking error banner */}
                    {bookingState === 'error' && (
                        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 shrink-0">
                            {bookingError || 'Booking failed'}
                            <button
                                onClick={() => setBookingState('idle')}
                                className="ml-2 underline"
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    {/* Selected slot + confirm button */}
                    {selectedSlot && bookingState !== 'success' && (
                        <div className="px-4 py-3 border-b bg-green-50 shrink-0">
                            <p className="text-xs font-medium text-green-800 mb-1.5">Selected slot:</p>
                            <p className="text-sm text-green-700">
                                {new Date(selectedSlot.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                {' · '}
                                {new Date(selectedSlot.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                {' – '}
                                {new Date(selectedSlot.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                {selectedSlot.employee_name && (
                                    <span className="text-green-600"> with {selectedSlot.employee_name}</span>
                                )}
                            </p>
                            {ctx.clientId ? (
                                <>
                                    {/* Service picker when no service from history */}
                                    {!ctx.service_id && services.length > 0 && (
                                        <div className="mt-2">
                                            <label className="text-xs font-medium text-orange-700 flex items-center gap-1 mb-1">
                                                <AlertTriangle size={11} />
                                                No service from history — select one:
                                            </label>
                                            <select
                                                value={manualServiceId}
                                                onChange={(e) => setManualServiceId(e.target.value)}
                                                className="w-full border border-orange-300 rounded px-2 py-1.5 text-xs focus:ring-purple-500 focus:border-purple-500 bg-white"
                                            >
                                                <option value="">Choose a service…</option>
                                                {services.map(s => (
                                                    <option key={s.service_id} value={s.service_id}>
                                                        {s.name} ({s.time_minutes}min — ${s.price})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <button
                                        onClick={handleBookAppointment}
                                        disabled={bookingState === 'booking' || !effectiveServiceId}
                                        className={`w-full mt-2 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                                            bookingState === 'booking' || !effectiveServiceId
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-green-600 text-white hover:bg-green-700'
                                        }`}
                                    >
                                        {bookingState === 'booking' ? (
                                            <>
                                                <Loader2 className="animate-spin" size={14} />
                                                Booking…
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={14} />
                                                Confirm Booking
                                            </>
                                        )}
                                    </button>
                                </>
                            ) : (
                                <p className="text-xs text-orange-600 mt-1.5">
                                    No client ID linked — copy this time and book manually in DaySmart.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Availability panel (scrollable) */}
                    <div className="flex-1 overflow-y-auto px-4 py-3">
                        <AvailabilityPanel
                            defaultLocationId={ctx.locationId}
                            defaultDuration={ctx.duration_minutes || undefined}
                            onSlotSelect={handleSlotSelect}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default StickyAvailabilityDrawer;
